import {
  ADVANCED,
  CROSS_SELL,
  CROSS_SELL_FIELDS,
  GUEST_CARD_REQUIREMENTS,
  GUEST_CARD_REQUIREMENTS_FIELDS,
  SELF_GUIDED_TOURS,
  SELF_GUIDED_TOURS_FIELDS,
  SELF_GUIDED_TOURS_FIELD_NAMES,
  VALIDATION_ERROR_MESSAGES,
} from './constants';
import { validateHttpsUrl } from '@Utils';

const {
  CROSS_SELL_RADIUS,
  CROSS_SELL_REFERRAL_SOURCE,
  NEARBY_PROPERTIES_EXCLUDED_PROPERTIES,
} = CROSS_SELL_FIELDS;

export const hasGCInfoRequirementsChanged = (
  originalValues = {},
  values = {},
) => {
  return GUEST_CARD_REQUIREMENTS_FIELDS.some(
    (fieldName) => originalValues[fieldName] !== values[fieldName],
  );
};

export const hasSelfGuidedToursChanged = (originalValues = {}, values = {}) => {
  return SELF_GUIDED_TOURS_FIELDS.some(
    (fieldName) => originalValues[fieldName] !== values[fieldName],
  );
};

export const hasExcludedPropertiesChanged = (
  originalExcludedProperties = [],
  excludedProperties = [],
) => {
  if (originalExcludedProperties.length !== excludedProperties.length) {
    return true;
  }
  return excludedProperties.some(
    (id) => !originalExcludedProperties.includes(id),
  );
};

export const hasCrossSellChanged = (originalValues = {}, values = {}) => {
  return (
    originalValues[CROSS_SELL_RADIUS] !== values[CROSS_SELL_RADIUS] ||
    originalValues[CROSS_SELL_REFERRAL_SOURCE] !==
      values[CROSS_SELL_REFERRAL_SOURCE] ||
    hasExcludedPropertiesChanged(
      originalValues[NEARBY_PROPERTIES_EXCLUDED_PROPERTIES],
      values[NEARBY_PROPERTIES_EXCLUDED_PROPERTIES],
    )
  );
};

export const getGCInfoRequirementChanges = (
  originalValues = {},
  values = {},
) => {
  return GUEST_CARD_REQUIREMENTS_FIELDS.reduce((updates, fieldName) => {
    const value = values[fieldName];
    const originalValue = originalValues[fieldName];

    if (value !== originalValue) {
      updates[fieldName] = value;
    }
    return updates;
  }, {});
};

export const getSelfGuidedToursChanges = (originalValues = {}, values = {}) => {
  return SELF_GUIDED_TOURS_FIELDS.reduce((updates, fieldName) => {
    const value = values[fieldName];
    const originalValue = originalValues[fieldName];

    if (value !== originalValue) {
      updates[fieldName] = value;
    }
    return updates;
  }, {});
};

export const getAdvancedSettingChanges = (
  advancedSettings,
  advancedSettingsTabData,
) => {
  const guestCardRequirementsUpdates = getGCInfoRequirementChanges(
    advancedSettings[GUEST_CARD_REQUIREMENTS],
    advancedSettingsTabData[GUEST_CARD_REQUIREMENTS],
  );

  const selfGuidedToursUpdates = getSelfGuidedToursChanges(
    advancedSettings[SELF_GUIDED_TOURS],
    advancedSettingsTabData[SELF_GUIDED_TOURS],
  );

  if (
    Object.keys(guestCardRequirementsUpdates).length === 0 &&
    Object.keys(selfGuidedToursUpdates).length === 0
  ) {
    return {};
  }

  return {
    [ADVANCED]: {
      [GUEST_CARD_REQUIREMENTS]: guestCardRequirementsUpdates,
      [SELF_GUIDED_TOURS]: selfGuidedToursUpdates,
    },
  };
};

export const getHasAdvancedSettingsChanged = (settings, settingsTabData) => {
  return (
    hasGCInfoRequirementsChanged(
      settings[GUEST_CARD_REQUIREMENTS],
      settingsTabData[GUEST_CARD_REQUIREMENTS],
    ) ||
    hasSelfGuidedToursChanged(
      settings[SELF_GUIDED_TOURS],
      settingsTabData[SELF_GUIDED_TOURS],
    ) ||
    hasCrossSellChanged(settings[CROSS_SELL], settingsTabData[CROSS_SELL])
  );
};

export const getValidationErrorMessages = (values) => {
  const urlFieldName = SELF_GUIDED_TOURS_FIELD_NAMES.SELF_GUIDED_TOUR_URL;
  const { [urlFieldName]: url } = values[SELF_GUIDED_TOURS] || {};

  if (!url || validateHttpsUrl(url)) {
    return null;
  }
  return {
    [urlFieldName]: VALIDATION_ERROR_MESSAGES.INVALID_URL,
  };
};
